/**
 * GangSheetCanvas — Phase 1 visual gang-sheet builder
 *
 * Features:
 *  - Fixed-width roll canvas (default 580 mm), growing height
 *  - Drag-to-reposition items
 *  - Real-time utilisation + cost panel
 *  - Magic Arrange (shelf bin-pack)
 *  - Collision detection (red glow + warning)
 *  - White-background detection + "Remove White" prompt
 *  - Dead Space Library suggestions when sheet is <70% full
 *  - Handoff callback with full layout metadata
 */

import { useState, useRef, useCallback, useEffect, useMemo, useId } from "react";
import { createPortal } from "react-dom";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  X,
  Wand2,
  ZoomIn,
  ZoomOut,
  AlertTriangle,
  Sparkles,
  ChevronDown,
  RotateCw,
  Info,
  CheckCircle2,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  type LocalItem,
  type CanvasConfig,
  computeCollisions,
  usedHeight,
  utilisation,
  heightToMetres,
  estimateCost,
  magicArrange,
  detectWhiteBackground,
  DEFAULT_CANVAS_CONFIG,
} from "@/lib/gang-sheet";

/* ── Constants ──────────────────────────────────────────────── */
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2.0;
const CANVAS_BG = "#f8f8f8";
const GRID_MM = 10; // guide grid pitch
const MIN_ITEM_MM = 10;

/* ── Helpers ────────────────────────────────────────────────── */
function uid(): string {
  return Math.random().toString(36).slice(2, 10);
}

function mmToPx(mm: number, zoom: number, dpmm: number): number {
  return mm * dpmm * zoom;
}

function pxToMm(px: number, zoom: number, dpmm: number): number {
  return px / (dpmm * zoom);
}

/** Derive natural display size in mm from pixel dimensions */
function naturalSizeMm(wPx: number, hPx: number, canvasWidthMm: number): { w: number; h: number } {
  // Default: scale so width fills 25% of canvas width
  const scale = (canvasWidthMm * 0.25) / wPx;
  return {
    w: Math.round(wPx * scale),
    h: Math.round(hPx * scale),
  };
}

/* ── Sub-components ─────────────────────────────────────────── */

function UtilBar({
  items,
  config,
  pricePerM,
}: {
  items: LocalItem[];
  config: CanvasConfig;
  pricePerM: number;
}) {
  const h = usedHeight(items);
  const canvasH = Math.max(h + config.min_spacing_mm, 50);
  const linM = heightToMetres(canvasH);
  const util = utilisation(items, config.canvas_width_mm, canvasH);
  const cost = estimateCost(canvasH, pricePerM);
  const hasItems = items.length > 0;

  const barColor = util > 80 ? "bg-emerald-500" : util > 50 ? "bg-amber-400" : "bg-primary/60";

  return (
    <div className="flex items-center gap-4 rounded-2xl border border-border bg-card px-4 py-2.5 shadow-soft text-sm flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <div className="h-2 w-32 overflow-hidden rounded-full bg-muted">
          <div
            className={cn("h-full rounded-full transition-all duration-300", barColor)}
            style={{ width: `${util}%` }}
          />
        </div>
        <span
          className={cn(
            "font-semibold tabular-nums",
            util > 80 ? "text-emerald-600" : "text-foreground",
          )}
        >
          {util.toFixed(1)}% used
        </span>
      </div>
      <div className="h-4 w-px bg-border" />
      <span className="text-muted-foreground">
        <span className="font-semibold text-foreground">{linM.toFixed(3)} m</span> linear
      </span>
      <div className="h-4 w-px bg-border" />
      {pricePerM > 0 ? (
        <span className="text-muted-foreground">
          Est. <span className="font-bold text-primary">£{cost.toFixed(2)}</span>{" "}
          <span className="text-xs">(£{pricePerM.toFixed(2)}/m)</span>
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">Pricing not configured</span>
      )}
      <div className="h-4 w-px bg-border" />
      <span className="text-muted-foreground">
        <span className="font-semibold text-foreground">{items.length}</span>{" "}
        {items.length === 1 ? "item" : "items"}
      </span>
      {!hasItems && (
        <span className="ml-auto text-xs text-muted-foreground italic">Drop artwork to start</span>
      )}
    </div>
  );
}

function DeadSpaceAlert({
  util,
  items,
  onDismiss,
}: {
  util: number;
  items: LocalItem[];
  onDismiss: () => void;
}) {
  if (items.length === 0 || util >= 70) return null;
  const h = usedHeight(items);
  const spareH = Math.max(0, Math.round(100 - (h % 1000)));
  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-800/50 dark:bg-amber-950/30"
    >
      <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
      <div className="flex-1">
        <span className="font-semibold text-amber-800 dark:text-amber-200">
          Sheet is only {util.toFixed(0)}% full.
        </span>{" "}
        <span className="text-amber-700 dark:text-amber-300">
          You have ~{spareH}mm of usable space left — consider adding more artwork to reduce waste
          and get better value per metre.
        </span>
      </div>
      <button onClick={onDismiss} className="text-amber-400 hover:text-amber-600">
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
}

/* ── Main component ─────────────────────────────────────────── */

export interface GangSheetCanvasProps {
  config?: Partial<CanvasConfig>;
  pricePerM?: number;
  /** Called when user clicks "Save & Continue" */
  onSave: (
    items: LocalItem[],
    heightMm: number,
    linearMetres: number,
    utilisationPct: number,
    estimatedCost: number,
  ) => void;
  onCancel?: () => void;
  initialItems?: LocalItem[];
  readOnly?: boolean;
}

export function GangSheetCanvas({
  config: configProp,
  pricePerM = 0,
  onSave,
  onCancel,
  initialItems = [],
  readOnly = false,
}: GangSheetCanvasProps) {
  const config: CanvasConfig = { ...DEFAULT_CANVAS_CONFIG, ...configProp };
  const DPMM = 1.5; // display pixels per mm at zoom=1

  const [items, setItems] = useState<LocalItem[]>(initialItems);
  const [zoom, setZoom] = useState(0.5);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{
    id: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const [resizing, setResizing] = useState<{
    id: string;
    startX: number;
    startY: number;
    origW: number;
    origH: number;
  } | null>(null);
  const [deadSpaceDismissed, setDeadSpaceDismissed] = useState(false);
  const [whiteDetecting, setWhiteDetecting] = useState(false);
  const canvasRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Zoom shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "=" || e.key === "+") setZoom((z) => Math.min(MAX_ZOOM, z + 0.1));
      if (e.key === "-") setZoom((z) => Math.max(MIN_ZOOM, z - 0.1));
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId && !readOnly) {
        removeItem(selectedId);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [selectedId, readOnly]);

  /* Derived values */
  const collisions = useMemo(
    () => computeCollisions(items, config.min_spacing_mm),
    [items, config.min_spacing_mm],
  );

  const canvasHeightMm = useMemo(() => {
    const h = usedHeight(items);
    return Math.max(h + 60, 200); // at least 200mm visible
  }, [items]);

  const util = useMemo(
    () =>
      utilisation(
        items,
        config.canvas_width_mm,
        Math.max(usedHeight(items) + config.min_spacing_mm, 50),
      ),
    [items, config],
  );

  /* ── File drop ──────────────────────────────────────────── */
  const onDrop = useCallback(
    async (accepted: File[]) => {
      if (readOnly) return;
      const newItems: LocalItem[] = [];
      for (const file of accepted) {
        const url = URL.createObjectURL(file);
        // Read natural dimensions
        const dims = await new Promise<{ w: number; h: number }>((res) => {
          const img = new Image();
          img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight });
          img.onerror = () => res({ w: 100, h: 100 });
          img.src = url;
        });
        const { w: wMm, h: hMm } = naturalSizeMm(dims.w, dims.h, config.canvas_width_mm);
        // Place below existing items
        const startY =
          usedHeight(newItems.length ? [...items, ...newItems] : items) + config.min_spacing_mm;
        const item: LocalItem = {
          id: uid(),
          file,
          preview_url: url,
          original_url: null,
          file_name: file.name,
          x_mm: config.min_spacing_mm,
          y_mm: startY,
          width_mm: wMm,
          height_mm: hMm,
          rotation_deg: 0,
          z_index: items.length + newItems.length,
          has_white_bg: false,
          white_removed: false,
          source_width_px: dims.w,
          source_height_px: dims.h,
          collision_ids: [],
        };
        newItems.push(item);
      }
      setItems((prev) => {
        const merged = [...prev, ...newItems];
        // Async white-bg detection
        detectWhiteBg(newItems);
        return merged;
      });
      setDeadSpaceDismissed(false);
    },
    [items, config, readOnly],
  );

  const detectWhiteBg = useCallback(async (newItems: LocalItem[]) => {
    setWhiteDetecting(true);
    const updates: Record<string, boolean> = {};
    for (const item of newItems) {
      updates[item.id] = await detectWhiteBackground(item.preview_url);
    }
    setItems((prev) =>
      prev.map((it) => (it.id in updates ? { ...it, has_white_bg: updates[it.id] } : it)),
    );
    setWhiteDetecting(false);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [], "application/pdf": [] },
    noClick: items.length > 0,
    noKeyboard: true,
  });

  /* ── Item mutations ─────────────────────────────────────── */
  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    setSelectedId((s) => (s === id ? null : s));
  }, []);

  const updateItem = useCallback((id: string, patch: Partial<LocalItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const removeWhiteBg = useCallback(
    (id: string) => {
      // Visual-only: mark white_removed so we overlay a checkerboard
      updateItem(id, { white_removed: true, has_white_bg: false });
    },
    [updateItem],
  );

  /* ── Magic Arrange ──────────────────────────────────────── */
  const handleMagicArrange = useCallback(() => {
    setItems((prev) => magicArrange(prev, config.canvas_width_mm, config.min_spacing_mm));
  }, [config]);

  /* ── Drag logic (pointer events) ───────────────────────── */
  const onPointerDownItem = useCallback(
    (e: React.PointerEvent, id: string) => {
      if (readOnly) return;
      e.stopPropagation();
      const item = items.find((it) => it.id === id);
      if (!item) return;
      setSelectedId(id);
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setDragging({ id, startX: e.clientX, startY: e.clientY, origX: item.x_mm, origY: item.y_mm });
    },
    [items, readOnly],
  );

  const onPointerMoveCanvas = useCallback(
    (e: React.PointerEvent) => {
      if (dragging) {
        const dx = pxToMm(e.clientX - dragging.startX, zoom, DPMM);
        const dy = pxToMm(e.clientY - dragging.startY, zoom, DPMM);
        const item = items.find((it) => it.id === dragging.id);
        if (!item) return;
        const newX = Math.max(
          0,
          Math.min(config.canvas_width_mm - item.width_mm, dragging.origX + dx),
        );
        const newY = Math.max(0, dragging.origY + dy);
        updateItem(dragging.id, { x_mm: newX, y_mm: newY });
      }
      if (resizing) {
        const dx = pxToMm(e.clientX - resizing.startX, zoom, DPMM);
        const dy = pxToMm(e.clientY - resizing.startY, zoom, DPMM);
        const newW = Math.max(MIN_ITEM_MM, resizing.origW + dx);
        const newH = Math.max(MIN_ITEM_MM, resizing.origH + dy);
        updateItem(resizing.id, { width_mm: newW, height_mm: newH });
      }
    },
    [dragging, resizing, zoom, items, config, updateItem],
  );

  const onPointerUpCanvas = useCallback(() => {
    setDragging(null);
    setResizing(null);
  }, []);

  const onPointerDownResize = useCallback(
    (e: React.PointerEvent, id: string) => {
      if (readOnly) return;
      e.stopPropagation();
      const item = items.find((it) => it.id === id);
      if (!item) return;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setResizing({
        id,
        startX: e.clientX,
        startY: e.clientY,
        origW: item.width_mm,
        origH: item.height_mm,
      });
    },
    [items, readOnly],
  );

  /* ── Rotate ─────────────────────────────────────────────── */
  const rotateItem = useCallback(
    (id: string) => {
      const item = items.find((it) => it.id === id);
      if (!item) return;
      // Swap width/height and rotate 90°
      updateItem(id, {
        rotation_deg: (item.rotation_deg + 90) % 360,
        width_mm: item.height_mm,
        height_mm: item.width_mm,
      });
    },
    [items, updateItem],
  );

  /* ── Save ────────────────────────────────────────────────── */
  const handleSave = useCallback(() => {
    const h = usedHeight(items);
    const canvasH = Math.max(h + config.min_spacing_mm, 50);
    const linM = heightToMetres(canvasH);
    const util2 = utilisation(items, config.canvas_width_mm, canvasH);
    const cost = estimateCost(canvasH, pricePerM);
    onSave(items, canvasH, linM, util2, cost);
  }, [items, config, pricePerM, onSave]);

  /* ── Render helpers ─────────────────────────────────────── */
  const canvasWPx = mmToPx(config.canvas_width_mm, zoom, DPMM);
  const canvasHPx = mmToPx(canvasHeightMm, zoom, DPMM);
  const hasCollisions = collisions.size > 0;

  /* White-bg items that haven't been resolved */
  const whiteItems = items.filter((it) => it.has_white_bg && !it.white_removed);

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* ── Toolbar ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 rounded-xl border border-border bg-card p-1">
          <button
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 0.1))}
            className="rounded-lg p-1.5 hover:bg-muted transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="min-w-[42px] text-center text-xs font-medium">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 0.1))}
            className="rounded-lg p-1.5 hover:bg-muted transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
        </div>

        {!readOnly && (
          <>
            <Button
              size="sm"
              variant="outline"
              className="rounded-xl gap-1.5"
              onClick={handleMagicArrange}
              disabled={items.length === 0}
            >
              <Wand2 className="h-3.5 w-3.5 text-primary" />
              Magic Arrange
            </Button>

            <label className="flex cursor-pointer items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-1.5 text-sm font-medium hover:bg-muted transition-colors">
              <Upload className="h-3.5 w-3.5" />
              Add artwork
              <input
                type="file"
                className="sr-only"
                multiple
                accept="image/*,.pdf"
                onChange={(e) => {
                  if (e.target.files) onDrop(Array.from(e.target.files));
                }}
              />
            </label>
          </>
        )}

        <div className="ml-auto flex items-center gap-2">
          {hasCollisions && (
            <span className="flex items-center gap-1.5 rounded-xl bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-600 border border-red-200 dark:bg-red-950/30 dark:border-red-800/50 dark:text-red-400">
              <AlertTriangle className="h-3.5 w-3.5" /> Overlap detected
            </span>
          )}
          {whiteDetecting && (
            <span className="text-xs text-muted-foreground animate-pulse">Analysing artwork…</span>
          )}
        </div>
      </div>

      {/* ── Utilisation bar ── */}
      <UtilBar items={items} config={config} pricePerM={pricePerM} />

      {/* ── White background alerts ── */}
      <AnimatePresence>
        {whiteItems.map((it) => (
          <motion.div
            key={it.id}
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm dark:border-blue-800/40 dark:bg-blue-950/30"
          >
            <Info className="h-4 w-4 shrink-0 text-blue-500" />
            <span className="flex-1 text-blue-800 dark:text-blue-200">
              <span className="font-semibold">{it.file_name}</span> appears to have a white
              background.
            </span>
            <button
              onClick={() => removeWhiteBg(it.id)}
              className="rounded-full border border-blue-300 bg-white px-3 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 transition-colors"
            >
              Remove white
            </button>
            <button
              onClick={() => updateItem(it.id, { has_white_bg: false })}
              className="text-blue-400 hover:text-blue-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>

      {/* ── Dead space alert ── */}
      <AnimatePresence>
        {!deadSpaceDismissed && (
          <DeadSpaceAlert util={util} items={items} onDismiss={() => setDeadSpaceDismissed(true)} />
        )}
      </AnimatePresence>

      {/* ── Canvas scroll area ── */}
      <div
        ref={scrollRef}
        className="relative flex-1 overflow-auto rounded-2xl border border-border bg-[#e8e8e8] shadow-inner"
        style={{ minHeight: 320 }}
      >
        {/* Drop overlay */}
        <div
          {...getRootProps()}
          className={cn(
            "absolute inset-0 z-10 flex items-center justify-center transition-all",
            isDragActive
              ? "bg-primary/10 border-2 border-dashed border-primary"
              : "pointer-events-none",
          )}
        >
          <input {...getInputProps()} />
          {isDragActive && (
            <div className="rounded-2xl bg-card px-8 py-6 shadow-pop text-center pointer-events-none">
              <Upload className="mx-auto mb-2 h-8 w-8 text-primary" />
              <p className="font-semibold">Drop artwork here</p>
            </div>
          )}
        </div>

        {/* Empty state */}
        {items.length === 0 && !isDragActive && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center"
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <div className="rounded-[2rem] border-2 border-dashed border-border/60 bg-card/60 px-10 py-8 shadow-soft">
              <Layers className="mx-auto mb-3 h-10 w-10 text-muted-foreground/40" />
              <p className="text-base font-semibold text-foreground/70">Build your gang sheet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Drag & drop artwork here, or click Add Artwork above
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Canvas: {config.canvas_width_mm}mm wide · {config.min_spacing_mm}mm spacing
              </p>
            </div>
          </div>
        )}

        {/* The actual canvas */}
        <div
          ref={canvasRef}
          className="relative select-none"
          style={{
            width: canvasWPx,
            height: canvasHPx,
            background: CANVAS_BG,
            backgroundImage: `
              linear-gradient(to right, #ddd 1px, transparent 1px),
              linear-gradient(to bottom, #ddd 1px, transparent 1px)
            `,
            backgroundSize: `${mmToPx(GRID_MM, zoom, DPMM)}px ${mmToPx(GRID_MM, zoom, DPMM)}px`,
            cursor: dragging ? "grabbing" : "default",
            margin: "0 auto",
          }}
          onPointerMove={onPointerMoveCanvas}
          onPointerUp={onPointerUpCanvas}
          onPointerLeave={onPointerUpCanvas}
          onClick={() => setSelectedId(null)}
        >
          {/* Width label */}
          <div
            className="absolute top-0 left-0 right-0 flex items-center justify-center pointer-events-none"
            style={{ height: mmToPx(6, zoom, DPMM) }}
          >
            <span className="text-[10px] text-muted-foreground/60 font-medium">
              {config.canvas_width_mm}mm
            </span>
          </div>

          {/* Items */}
          {items.map((item) => {
            const isSelected = selectedId === item.id;
            const hasCollision = (collisions.get(item.id)?.length ?? 0) > 0;
            const x = mmToPx(item.x_mm, zoom, DPMM);
            const y = mmToPx(item.y_mm, zoom, DPMM);
            const w = mmToPx(item.width_mm, zoom, DPMM);
            const h = mmToPx(item.height_mm, zoom, DPMM);

            return (
              <div
                key={item.id}
                style={{
                  position: "absolute",
                  left: x,
                  top: y,
                  width: w,
                  height: h,
                  transform: `rotate(${item.rotation_deg}deg)`,
                  cursor: readOnly ? "default" : "grab",
                  zIndex: isSelected ? 100 : item.z_index + 1,
                  boxSizing: "border-box",
                  border: hasCollision
                    ? "2px solid #ef4444"
                    : isSelected
                      ? "2px solid hsl(var(--primary))"
                      : "1px dashed #ccc",
                  borderRadius: 3,
                  boxShadow: hasCollision
                    ? "0 0 0 4px rgba(239,68,68,0.25)"
                    : isSelected
                      ? "0 0 0 3px hsla(var(--primary)/0.2)"
                      : "none",
                  overflow: "hidden",
                  transition: dragging?.id === item.id ? "none" : "box-shadow 0.15s",
                }}
                onPointerDown={(e) => onPointerDownItem(e, item.id)}
              >
                {/* Artwork preview */}
                <img
                  src={item.preview_url}
                  alt={item.file_name ?? "artwork"}
                  draggable={false}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "fill",
                    display: "block",
                    mixBlendMode: item.white_removed ? "multiply" : "normal",
                    background: item.white_removed
                      ? "repeating-conic-gradient(#ccc 0% 25%, white 0% 50%) 0 0 / 8px 8px"
                      : "transparent",
                  }}
                />

                {/* Collision pulse overlay */}
                {hasCollision && (
                  <div className="absolute inset-0 animate-pulse bg-red-500/20 pointer-events-none" />
                )}

                {/* Selected controls */}
                {isSelected && !readOnly && (
                  <>
                    {/* Remove */}
                    <button
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeItem(item.id);
                      }}
                      className="absolute -right-2 -top-2 z-50 rounded-full bg-card border border-border p-0.5 shadow hover:bg-red-50"
                    >
                      <X className="h-3 w-3 text-muted-foreground" />
                    </button>
                    {/* Rotate */}
                    <button
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        rotateItem(item.id);
                      }}
                      className="absolute -left-2 -top-2 z-50 rounded-full bg-card border border-border p-0.5 shadow hover:bg-primary/10"
                    >
                      <RotateCw className="h-3 w-3 text-muted-foreground" />
                    </button>
                    {/* Resize handle */}
                    <div
                      onPointerDown={(e) => onPointerDownResize(e, item.id)}
                      style={{
                        position: "absolute",
                        bottom: -4,
                        right: -4,
                        width: 10,
                        height: 10,
                        background: "hsl(var(--primary))",
                        borderRadius: 2,
                        cursor: "se-resize",
                        zIndex: 50,
                      }}
                    />
                  </>
                )}

                {/* Dimensions label */}
                {isSelected && (
                  <div
                    className="absolute bottom-0 left-0 right-0 pointer-events-none"
                    style={{ background: "rgba(0,0,0,0.45)" }}
                  >
                    <span className="block text-center text-[9px] text-white px-1 leading-4">
                      {Math.round(item.width_mm)}×{Math.round(item.height_mm)}mm
                    </span>
                  </div>
                )}
              </div>
            );
          })}

          {/* Canvas bottom label */}
          {items.length > 0 && (
            <div
              className="absolute left-0 right-0 pointer-events-none flex items-center justify-center"
              style={{ top: mmToPx(usedHeight(items) + config.min_spacing_mm, zoom, DPMM) }}
            >
              <span className="rounded bg-primary/10 px-2 py-0.5 text-[10px] text-primary/70 font-medium">
                ↑ {Math.round(usedHeight(items) + config.min_spacing_mm)}mm used
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Collision warning banner ── */}
      <AnimatePresence>
        {hasCollisions && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 dark:border-red-800/50 dark:bg-red-950/30 dark:text-red-300"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="font-medium">Artwork overlap detected.</span>{" "}
            <span>
              Items that overlap are shown with a red glow. Reposition or use Magic Arrange to fix.
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Footer actions ── */}
      {!readOnly && (
        <div className="flex items-center justify-between gap-3">
          {onCancel && (
            <Button variant="ghost" className="rounded-full" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <div className="flex items-center gap-2 text-xs text-muted-foreground ml-auto">
            {items.length > 0 && !hasCollisions && (
              <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-3.5 w-3.5" /> Layout looks good
              </span>
            )}
          </div>
          <Button
            className="rounded-full bg-gradient-hero px-6 text-primary-foreground shadow-soft hover:opacity-90 gap-1.5"
            onClick={handleSave}
            disabled={items.length === 0}
          >
            <CheckCircle2 className="h-4 w-4" />
            Save sheet & continue
          </Button>
        </div>
      )}
    </div>
  );
}
